//FlyerOnTheWall
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import validateManyFields from '../../validations';
import '../../styles/Concerns.css';
import '../../styles/Alert.css';

const FlyerOnTheWall = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const authState = useSelector(state => state.authReducer);

  const { bookId, bookTitle, ownerId, ownerName, flyerOnTheWallId, flyerOnTheWallName } = location.state || {};
  const [fetchData, { loading }] = useFetch();

  const [think5whysCode, setThink5whysCode] = useState("");
  const [flyerName, setFlyerName] = useState("");
  const [flyerIsvalidated, setFlyerIsvalidated] = useState(false);
  const [thinkWhysCodeVerificationMsg, setThinkWhysCodeVerificationMsg] = useState("");

  const [formData, setFormData] = useState({
    bookId: bookId || "",
    bookTitle: bookTitle || "",
    ownerId: ownerId || "",
    ownerName: ownerName || "",
    ownerCode: authState.user.think5whyscode || "",
    flyerId: flyerOnTheWallId || "",
    flyerName: flyerOnTheWallName || "",
    flyerCode: think5whysCode || "",
    think5whyscode: "",
    isAllowed: true,
    isActive: true,
    isDeleted: false,
    createdBy: ownerId || "",
    dateCreated: new Date().toISOString(),
    canComment: true,
    comments: null,
    remarks: flyerOnTheWallName+"Flyer on the Wall for "+bookTitle,
  });

  const [formErrors, setFormErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);

  const mode = flyerOnTheWallId ? "update" : "add";

  useEffect(() => {
    document.title = mode === "update" ? "Update" : "Add";
  }, [mode]);

  useEffect(() => {
    if (mode === "update") {
      const config = { url: `/flyerOnTheWalls/${flyerOnTheWallId}`, method: "get", headers: { Authorization: authState.token } };
      fetchData(config, { showSuccessToast: false }).then((data) => {
        const flyerData = data.flyerOnTheWall;
        const jsDate = new Date(flyerData.dateCreated);
        const formattedDate = `${jsDate.getDate().toString().padStart(2, '0')}-${(jsDate.getMonth() + 1).toString().padStart(2, '0')}-${jsDate.getFullYear()}`;
        setFormData({
          ...flyerData,
          dateCreated: formattedDate,
        });
      }).catch(error => {
        console.error('Error fetching flyerOnTheWall:', error);
      });
    }
  }, [mode, authState.token, flyerOnTheWallId, fetchData]);

  const fetchThink5WhysCodeData = () => {
    const flyerCode = formData.think5whyscode;
    const config = {
      url: "/auth/verifyt5wcode",
      method: "post",
      data: { think5whyscode: flyerCode }
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setFlyerIsvalidated(true);
          setThinkWhysCodeVerificationMsg("Flyer Code is Valid");
          setFormData({
            ...formData,
            flyerId: response.t5wcode._id,
            flyerName: response.t5wcode.name,
            flyerCode: response.t5wcode.think5whyscode,
            remarks: response.t5wcode.name+" is Flyer on the Wall for "+bookTitle,
          });
        } else {
          setFlyerIsvalidated(false);
          setThinkWhysCodeVerificationMsg("Flyer Code not found!!");
        }
      })
      .catch(error => {
        setFlyerIsvalidated(false);
        setThinkWhysCodeVerificationMsg("Flyer Code not found!!");
      });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleReset = e => {
    e.preventDefault();
    if (flyerOnTheWallId) {
      setFormData({
        ...formData,
        flyerId: flyerOnTheWallId,
        flyerName: flyerOnTheWallName, 
      });
    }
  };

  const handleVerify = (e) => {
    e.preventDefault();
    if (formData.think5whyscode) {
      fetchThink5WhysCodeData();
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    const errors = validateManyFields("flyerOnTheWall", formData);
    setFormErrors({});
    if (errors.length > 0) {
      setFormErrors(errors.reduce((total, ob) => ({ ...total, [ob.field]: ob.err }), {}));
      return;
    }

    const config = {
      url: mode === "update" ? `/flyonwall/${flyerOnTheWallId}` : "/flyonwall",
      method: mode === "update" ? "put" : "post",
      data: formData,
      headers: { Authorization: authState.token }
    };

    fetchData(config).then(() => {
      navigate('/flyersonthewall', { state: { bookId: bookId, bookTitle: bookTitle, ownerId: ownerId, ownerName: ownerName, fromPage: "flyer" } });
    }).catch(error => {
      console.error('Error submitting form data:', error);
    });
  };

  const fieldError = (field) => (
    <p className={`mt-1 text-pink-600 text-sm ${formErrors[field] ? "block" : "hidden"}`}>
      <i className='mr-2 fa-solid fa-circle-exclamation'></i>
      {formErrors[field]}
    </p>
  );

  // const handleFlyerOnTheWallListClick = () => {
  //   navigate('/flyersonthewall', { state: { bookId: bookId, bookTitle: bookTitle, ownerId: ownerId, ownerName: ownerName, fromPage: "flyer" } });
  // };

  return (
    <MainLayout>
      <div className="min-h-screen py-8 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 mt-12">
        <h3 className='text-xl font-bold text-white text-center mb-4'>Manage Flyer On The Wall</h3>
        <form className='m-auto my-2 p-2 border-2 shadow-md rounded-md md:w-7/12 w-full'>
          {loading ? (
            <Loader />
          ) : (
            <div className='text-white text-m'>
              <h2 className='flex flex-row justify-center items-center text-white'>{mode === "update" ? "Update" : "Add"}</h2>
                <div className="mb-4 text-white">
                  <p>Input 12 character Think5Whys Code of the Person to add in "Flying On The Wall" List for Your Book - </p>
                  <p className="mt-4 mb-4 text-white"><strong>{formData.bookTitle}</strong></p>
                  <p className="mt-4 mb-4 text-yellow-200">Think5Whys Code of the Person can be found in <strong>"My Think5Whys Code"</strong> under the <strong>"Profile"</strong> module.</p>
                  <label htmlFor="think5whyscode" className="mb-4 text-white">My Think5Whys Code:</label>
                  <br />
                  <input
                    type="text"
                    name="think5whyscode"
                    id="think5whyscode"
                    value={formData.think5whyscode}
                    onChange={handleChange}
                    style={{ border: '1px solid #ccc', padding: '8px', width: '50%' }}
                  />
                  <br />
                  <button className='mt-4 w-40' onClick={handleVerify}>Verify</button>
                  <div className={flyerIsvalidated ? 'text-green-500' : 'text-orange-500'}>
                    {thinkWhysCodeVerificationMsg}
                  </div>
                </div>
                {flyerIsvalidated && (
                  <>
                    <div className="mb-4">
                      <p className="mb-4">For the Book:  <strong className="text-yellow-200">{formData.bookTitle}</strong></p>
                      <p >Name of the Flyer you are going to Add is: <strong className="text-yellow-200">{formData.flyerName}</strong></p>
                    </div>
                    <div className="mb-4">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.isAllowed}
                          onChange={handleChange}
                          name="isAllowed"
                          className="mr-1"
                        />
                        Is Allowed to Read Book
                      </label>
                    </div>
                    <div className="mb-4">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.canComment}
                          onChange={handleChange}
                          disabled={true}
                          name="canComment"
                          className="mr-1"
                        />
                        Can Comment on Book
                      </label>
                    </div>
                    <div className="mb-4">
                      <label htmlFor="remarks">Remarks (if any):</label>
                      <textarea 
                        name="remarks" 
                        value={formData.remarks} 
                        onChange={handleChange} 
                        maxLength="100"
                        style={{
                          border: '1px solid #ccc',
                          padding: '2px',
                          backgroundColor: '#f0f0f0', 
                          color: '#000000',
                          maxWidth: '650px', 
                          maxHeight: '150px' 
                        }}
                      />
                    </div>
                    {fieldError("remarks")}
                  </>
                )}
              <div className="border-container-gray mx-2">
                <div className="flex flex-row items-center gap-2 mt-2">
                  <button onClick={handleSubmit}>{mode === "update" ? "Update" : "Add"}</button>
                  <button onClick={handleReset}>Reset</button>
                  <button onClick={() => navigate(-1)}>Back</button>
                </div>
              </div>          
            </div>
          )}
        </form>
        {showAlert && (
          <div className="alert">
            <p>{mode === "update" ? "Updated" : "Added"} successfully!</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default FlyerOnTheWall;
